import { useState } from 'react'
import { Flex, Text, Button, Icon, Box, Select, Grid, Heading } from '@chakra-ui/react'
import { createListCollection } from '@chakra-ui/react'
import { RadioCard } from '@chakra-ui/react'
import { LuCloudDownload, LuFileSpreadsheet, LuPaperclip, LuTable } from 'react-icons/lu'
import {
  exportMediations,
  exportActivities,
  exportActivitiesZip,
  exportMediationAttachments,
} from '@/api/export.api'
import { toast } from '@/components/ui/toaster/toasterHelpers'

function translateExportError(e: any, year?: number, month?: number) {
  const msg = e?.backendMessage || ''

  // Mediation error
  if (msg.includes('No approved mediation orders')) {
    if (month) {
      const monthStr = String(month).padStart(2, '0')
      return `Für den Zeitraum ${monthStr}/${year} wurden keine genehmigten Mediationsaufträge gefunden.`
    }
    return `Für das Dienstjahr ${year} wurden keine genehmigten Mediationsaufträge gefunden.`
  }

  // Activities error
  if (msg.includes('No activities found for the period')) {
    const monthStr = String(month).padStart(2, '0')
    return `Für den Zeitraum ${monthStr}/${year} wurden keine Aktivitäten gefunden.`
  }

  return 'Export fehlgeschlagen.'
}

export default function UnionExportPage() {
  const currentYear = new Date().getFullYear()

  // STATES
  const [fileTypeMediations, setFileTypeMediations] = useState<'excel' | 'csv' | 'attachments'>(
    'excel'
  )
  const [fileTypeActivities, setFileTypeActivities] = useState<'excel' | 'csv' | 'zip'>('excel')
  const [yearMediations, setYearMediations] = useState(currentYear)
  const [monthMediations, setMonthMediations] = useState(new Date().getMonth() + 1)
  const [yearActivities, setYearActivities] = useState(currentYear)
  const [monthActivities, setMonthActivities] = useState(new Date().getMonth() + 1)

  const [loadingM, setLoadingM] = useState(false)
  const [loadingA, setLoadingA] = useState(false)

  const years = [currentYear - 1, currentYear, currentYear + 1]

  const yearOptions = createListCollection({
    items: years.map(y => ({ label: String(y), value: String(y) })),
  })

  const monthNames = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ]
  const monthOptions = createListCollection({
    items: monthNames.map((name, idx) => ({ label: name, value: String(idx + 1) })),
  })

  function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  async function downloadMediations() {
    try {
      setLoadingM(true)

      const monthStr = String(monthMediations).padStart(2, '0')

      // attachments ZIP
      if (fileTypeMediations === 'attachments') {
        // SIB-866: the attachments endpoint has no month param — filter the
        // selected month via its from/to date range instead
        const lastDay = new Date(yearMediations, monthMediations, 0).getDate()
        const { blob, filename } = await exportMediationAttachments({
          from: `${yearMediations}-${monthStr}-01`,
          to: `${yearMediations}-${monthStr}-${String(lastDay).padStart(2, '0')}`,
        })
        triggerDownload(
          blob,
          filename || `bestellungen_anhaenge_${yearMediations}_${monthStr}.zip`
        )
        return
      }

      // excel/csv export (existing endpoint)
      const { blob, filename } = await exportMediations(yearMediations, monthMediations)
      const ext = fileTypeMediations === 'excel' ? 'xlsx' : 'csv'
      triggerDownload(blob, filename || `vermittlungen_${yearMediations}_${monthStr}.${ext}`)
    } catch (err: any) {
      const msg = translateExportError(err, yearMediations, monthMediations)
      toast.error(msg)
    } finally {
      setLoadingM(false)
    }
  }

  async function downloadActivities() {
    try {
      setLoadingA(true)

      const monthStr = String(monthActivities).padStart(2, '0')

      // ZIP with PDFs export (slower endpoint)
      if (fileTypeActivities === 'zip') {
        const { blob, filename } = await exportActivitiesZip(yearActivities, monthActivities)
        const fallbackFilename = `Aktivitaeten_Export_${yearActivities}_${monthStr}.zip`
        triggerDownload(blob, filename || fallbackFilename)
        return
      }

      // Excel/CSV export (fast endpoint)
      const format = fileTypeActivities === 'excel' ? 'xlsx' : 'csv'
      const { blob, filename } = await exportActivities(yearActivities, monthActivities, format)
      const fallbackFilename = `Aktivitaeten_Export_${yearActivities}_${monthStr}.${format}`
      triggerDownload(blob, filename || fallbackFilename)
    } catch (err: any) {
      const msg = translateExportError(err, yearActivities, monthActivities)
      toast.error(msg)
    } finally {
      setLoadingA(false)
    }
  }

  // COMPONENT FOR FORMAT RADIO CARDS
  const FormatSelector = <T extends 'excel' | 'csv' | 'attachments' | 'zip'>({
    value,
    onChange,
    showAttachments = false,
    showZipWithPdfs = false,
  }: {
    value: T
    onChange: (v: T) => void
    showAttachments?: boolean
    showZipWithPdfs?: boolean
  }) => (
    <RadioCard.Root
      value={value}
      onValueChange={details => onChange(details.value as T)}
      colorPalette="gray"
      orientation="horizontal"
      align="center"
      mt={3}
      width="full" //  take full card width
    >
      <Grid
        templateColumns={showAttachments ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)'}
        gap={4}
        width="full" // no empty space on the right
        alignItems="stretch"
      >
        {/* Excel */}
        <RadioCard.Item value="excel" width="full">
          <RadioCard.ItemHiddenInput />
          <RadioCard.ItemControl
            width="full"
            minH="120px" // equal card height
            display="flex"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
          >
            <RadioCard.ItemContent>
              <Icon as={LuFileSpreadsheet} boxSize={8} />
              <RadioCard.ItemText fontSize="md" mt={2}>
                Excel Tabelle
              </RadioCard.ItemText>
            </RadioCard.ItemContent>
            <RadioCard.ItemIndicator />
          </RadioCard.ItemControl>
        </RadioCard.Item>

        {/* CSV */}
        <RadioCard.Item value="csv" width="full">
          <RadioCard.ItemHiddenInput />
          <RadioCard.ItemControl
            width="full"
            minH="120px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
          >
            <RadioCard.ItemContent>
              <Icon as={LuTable} boxSize={8} />
              <RadioCard.ItemText fontSize="md" mt={2}>
                .csv-Datei
              </RadioCard.ItemText>
            </RadioCard.ItemContent>
            <RadioCard.ItemIndicator />
          </RadioCard.ItemControl>
        </RadioCard.Item>

        {/* Attachments (only for Bestellungen) */}
        {showAttachments && (
          <RadioCard.Item value="attachments" width="full">
            <RadioCard.ItemHiddenInput />
            <RadioCard.ItemControl
              width="full"
              minH="120px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
            >
              <RadioCard.ItemContent>
                <Icon as={LuPaperclip} boxSize={8} />
                <RadioCard.ItemText fontSize="md" mt={2}>
                  Anhänge (ZIP)
                </RadioCard.ItemText>
              </RadioCard.ItemContent>
              <RadioCard.ItemIndicator />
            </RadioCard.ItemControl>
          </RadioCard.Item>
        )}

        {/* ZIP with PDFs (only for Aktivitäten) */}
        {showZipWithPdfs && (
          <RadioCard.Item value="zip" width="full">
            <RadioCard.ItemHiddenInput />
            <RadioCard.ItemControl
              width="full"
              minH="120px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
            >
              <RadioCard.ItemContent>
                <Icon as={LuPaperclip} boxSize={8} />
                <RadioCard.ItemText fontSize="md" mt={2}>
                  ZIP mit PDFs
                </RadioCard.ItemText>
              </RadioCard.ItemContent>
              <RadioCard.ItemIndicator />
            </RadioCard.ItemControl>
          </RadioCard.Item>
        )}
      </Grid>
    </RadioCard.Root>
  )

  // COMPONENT FOR YEAR SELECT
  const YearSelect = ({ year, setYear }: { year: number; setYear: (y: number) => void }) => (
    <Select.Root
      collection={yearOptions}
      value={[String(year)]}
      onValueChange={d => setYear(Number(d.value[0]))}
      width="50%"
    >
      <Select.Trigger>
        <Select.ValueText />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Positioner zIndex={1000}>
        <Select.Content>
          {yearOptions.items.map(it => (
            <Select.Item key={it.value} item={it.value}>
              <Select.ItemText>{it.label}</Select.ItemText>
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  )

  // COMPONENT FOR MONTH SELECT
  const MonthSelect = ({ month, setMonth }: { month: number; setMonth: (m: number) => void }) => (
    <Select.Root
      collection={monthOptions}
      value={[String(month)]}
      onValueChange={d => setMonth(Number(d.value[0]))}
      width="50%"
    >
      <Select.Trigger>
        <Select.ValueText />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Positioner zIndex={1000}>
        <Select.Content>
          {monthOptions.items.map(it => (
            <Select.Item key={it.value} item={it.value}>
              <Select.ItemText>{it.label}</Select.ItemText>
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  )

  return (
    <Flex data-testid="data-export-page" direction="column" p={4} gap={4} w="full">
      <Heading size="lg" fontWeight="normal">
        Datenexport
      </Heading>

      <Flex direction="row" gap={4} w="full" flex="1">
        {/* CARD 1 — Mediation */}
        <Box
          data-testid="export-orders-section"
          bg="bg"
          borderRadius="xl"
          p={6}
          borderColor="border"
          borderWidth="1px"
          flex="1"
        >
          <Text fontSize="md" fontWeight="700" mb={4}>
            Bestellungen
          </Text>

          <Text fontSize="sm" fontWeight="normal" mb={2} color="fg.muted">
            Alle Bestellungen herunterladen
          </Text>
          <Flex gap={4} mb={2}>
            <MonthSelect month={monthMediations} setMonth={setMonthMediations} />
            <YearSelect year={yearMediations} setYear={setYearMediations} />
          </Flex>

          <FormatSelector
            value={fileTypeMediations}
            onChange={setFileTypeMediations}
            showAttachments
          />

          <Button
            size="sm"
            mt={4}
            colorPalette="teal"
            onClick={downloadMediations}
            loading={loadingM}
          >
            <LuCloudDownload /> Herunterladen
          </Button>
        </Box>

        {/* CARD 2 — Activities */}
        <Box
          data-testid="export-activities-section"
          bg="bg"
          borderRadius="xl"
          p={6}
          borderColor="border"
          borderWidth="1px"
          flex="1"
        >
          <Text fontSize="md" fontWeight="700" mb={4}>
            Aktivitäten
          </Text>

          <Text fontSize="sm" fontWeight="normal" mb={2} color="fg.muted">
            Alle Aktivitäten herunterladen
          </Text>
          <Flex gap={4} mb={2}>
            <MonthSelect month={monthActivities} setMonth={setMonthActivities} />
            <YearSelect year={yearActivities} setYear={setYearActivities} />
          </Flex>
          <FormatSelector
            value={fileTypeActivities}
            onChange={setFileTypeActivities}
            showZipWithPdfs
          />

          <Button
            size="sm"
            mt={4}
            colorPalette="teal"
            onClick={downloadActivities}
            loading={loadingA}
          >
            <LuCloudDownload /> Herunterladen
          </Button>
        </Box>
      </Flex>
    </Flex>
  )
}
